// ─── useHistory ────────────────────────────────────────────────────────────────
// History management: delete, confirm output, inline editing, content memory,
// and the download actions of a history card.
//
// Downloads read the entry's PIECE LIST (src/publish/), never content.full_text:
//   downloadAs(entry, id)   → builds the file of a download id of the catalogue
//                             (publish/downloads.js :: buildDownload) and saves it
//                             through publish/downloads.js :: downloadBlob, the
//                             save path the console shares. Resolves TRUE when
//                             a file was saved and FALSE otherwise; it never
//                             rejects: a failed build (unknown id, a serialiser
//                             that threw, a chunk that did not load) saves nothing
//                             and says nothing — no server text, no alert. The
//                             caller counts the entry as downloaded only on true.
//   exportEntry(entry)      → the paste-ready text file ('ready-txt'), the one
//                             action of Step4Publish.
// The .docx / .pdf serialisers load on demand inside buildDownload.
// The hook has no text accessor: the card's copy button (outputs/pieces/
// ReadyToPublish.jsx) reads the piece list itself, and a reader of the whole
// output calls utils/formatters.js :: getFullOutputTextForEntry directly.

import { useCallback } from 'react';
import { buildDownload, downloadBlob } from '../publish/downloads.js';

export function useHistory({ state, dispatch, contentMemoryRef }) {

  const deleteEntry = useCallback((entryId) => {
    dispatch({ type: 'DELETE_HISTORY_ENTRY', entryId });
    contentMemoryRef.current = contentMemoryRef.current.filter(m => m.id !== entryId);
  }, [dispatch, contentMemoryRef]);

  const confirmOutput = useCallback((entry) => {
    dispatch({ type: 'SET_BATCH', payload: { confirmedOutput: entry, wizardStep: 3 } });
  }, [dispatch]);

  const handleEditSave = useCallback((entryId) => {
    const { editedContent } = state;
    const fields = editedContent[entryId] || {};
    if (!Object.keys(fields).length) {
      dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: null });
      return;
    }

    // Apply field edits to the history entry
    dispatch({ type: 'SET_KEY', key: 'outputHistory', value: prev =>
      (prev || []).map(e => {
        if (e.id !== entryId) return e;
        const updatedResults = applyFieldEdits({ ...e.results }, fields);
        const validationStatus = {};
        Object.keys(fields).forEach(k => { validationStatus[k] = 'edited'; });
        dispatch({ type: 'SET_KEY', key: 'editValidationStatus', value: prev2 => ({ ...prev2, [entryId]: { ...(prev2[entryId] || {}), ...validationStatus } }) });
        return { ...e, results: updatedResults };
      })
    });
    dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: null });
  }, [dispatch, state]);

  const handleEditCancel = useCallback((entryId) => {
    dispatch({ type: 'SET_BATCH', payload: {
      editingEntryId: null,
      editedContent: { ...state.editedContent, [entryId]: {} },
    }});
  }, [dispatch, state.editedContent]);

  const toggleEditMode = useCallback((entryId) => {
    const isCurrentlyEditing = state.editingEntryId === entryId;
    if (isCurrentlyEditing) {
      handleEditSave(entryId);
    } else {
      dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: entryId });
    }
  }, [state.editingEntryId, handleEditSave, dispatch]);

  const downloadAs = useCallback((entry, id) =>
    buildDownload(entry, id)
      .then(({ blob, filename }) => { downloadBlob(blob, filename); return true; })
      .catch(() => false),
  []);

  const exportEntry = useCallback((entry) => downloadAs(entry, 'ready-txt'), [downloadAs]);

  return {
    deleteEntry,
    confirmOutput,
    handleEditSave,
    handleEditCancel,
    toggleEditMode,
    exportEntry,
    downloadAs,
  };
}

// ─── Edit Helpers ─────────────────────────────────────────────────────────────

function applyFieldEdits(results, fields) {
  const updated = { ...results };
  for (const [path, value] of Object.entries(fields)) {
    const parts = path.split('.');
    if (parts.length === 1) {
      updated[parts[0]] = value;
    } else if (parts.length === 3 && parts[0] === 'slides') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.slides)) {
        updated.slides = updated.slides.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      }
    } else if (parts.length === 3 && parts[0] === 'sections') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.sections)) {
        updated.sections = updated.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      }
    } else if (parts.length === 5 && parts[0] === 'sections' && parts[2] === 'subsections') {
      const si = parseInt(parts[1], 10);
      const ssi = parseInt(parts[3], 10);
      const field = parts[4];
      if (Array.isArray(updated.sections)) {
        updated.sections = updated.sections.map((s, i) => {
          if (i !== si) return s;
          return { ...s, subsections: (s.subsections || []).map((ss, j) => j === ssi ? { ...ss, [field]: value } : ss) };
        });
      }
    } else if (parts.length === 3 && parts[0] === 'ads') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.ads)) {
        updated.ads = updated.ads.map((a, i) => i === idx ? { ...a, [field]: value } : a);
      }
    } else {
      // Nested dot path (hero.headline, problem.content, etc.)
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (obj[parts[i]] && typeof obj[parts[i]] === 'object') obj = obj[parts[i]];
        else break;
      }
      obj[parts[parts.length - 1]] = value;
    }
  }
  return updated;
}
